import React, { useRef } from 'react';
import { OverlayContainer } from 'react-aria';
import { Transition } from 'react-transition-group';
import { TransitionProps } from 'react-transition-group/Transition';
import { BACKDROP_TRANSITION_DURATION_MILLIS } from './constants';
import * as S from './styles';
import { OverlayProps } from './types';

export interface BackdropProps
  extends Omit<OverlayProps, 'transitionStatus'>,
    Pick<TransitionProps<HTMLDivElement>, 'onExited'> {
  children?: React.ReactNode;
  className?: string;
  /**
   * Whether or not the overlay should be displayed
   */
  isOpen?: boolean;
  /**
   * If `true`, indicates that the Backdrop will be used as an overlay.  An overlay
   * is rendered inside of an `OverlayContainer` which uses a React Portal to render
   * the Backdrop at the end of the document body.  The `OverlayContainer` will also
   * manage multiple overlays, adding `aria-hidden` to the ones underneath the topmost
   * overlay.
   *
   * When in "overlay" mode, the backdrop will also hide the page's scrollbars to prevent
   * scrolling on the underlying page while the backdrop is open.
   *
   * See react-aria's <a href="https://react-spectrum.adobe.com/react-aria/useDialog.html" target="_blank">useDialog</a>
   * docs for more details.
   */
  overlay?: boolean;
  /**
   * If `overlay === true`, scrolling will automatically be prevented, however the
   * `preventScrolling` prop offers more control over when scrolling is prevented
   * and will always "win" over the `overlay` prop.
   */
  preventScrolling?: boolean;
}

/**
 * Backdrop to use for overlays, modals, dialogs, etc.
 */
const Backdrop: React.FC<BackdropProps> = ({
  className,
  children,
  contentsPosition,
  onClick,
  isOpen,
  onExited,
  onPointerDown,
  overlay,
  preventScrolling,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Transition
      in={isOpen}
      onExited={onExited}
      mountOnEnter
      nodeRef={nodeRef}
      timeout={BACKDROP_TRANSITION_DURATION_MILLIS}
      unmountOnExit
    >
      {(transitionStatus) => {
        const backdrop = (
          <S.Overlay
            {...{
              className,
              contentsPosition,
              onClick,
              onPointerDown,
              transitionStatus,
            }}
            preventScrolling={preventScrolling ?? overlay}
            ref={nodeRef}
          >
            {children}
          </S.Overlay>
        );

        if (!overlay) {
          return backdrop;
        }

        return <OverlayContainer>{backdrop}</OverlayContainer>;
      }}
    </Transition>
  );
};

export default Backdrop;
